import { useEffect, useRef, useState } from 'react';

interface ConfettiPiece {
  id: number;
  x: number;
  color: string;
  delay: number;
  duration: number;
}

interface SuccessConfettiProps {
  trigger: boolean;
  onComplete?: () => void;
}

const COLORS = [
  'hsl(37, 45%, 60%)', // gold
  'hsl(37, 50%, 70%)', // gold-glow
  'hsl(142, 71%, 45%)', // accent green
  'hsl(0, 0%, 100%)', // white
  'hsl(217, 91%, 60%)', // blue
];

export function SuccessConfetti({ trigger, onComplete }: SuccessConfettiProps) {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);
  const [isActive, setIsActive] = useState(false);
  // The callback through a ref so its identity cannot restart the
  // effect — see below for why that mattered.
  const done = useRef(onComplete);
  done.current = onComplete;

  /**
   * BUGFIX — `onComplete` never fired, so anything waiting on it
   * waited forever.
   *
   * The effect used to depend on `[trigger, isActive, onComplete]`
   * and guard with `if (trigger && !isActive)`. The first run set
   * `isActive` true, which changed a dependency, which ran the
   * CLEANUP — clearing the 3.5s timer — and re-ran the effect, where
   * the guard was now false, so no new timer was ever scheduled. A
   * caller that re-rendered (which a parent holding `showConfetti`
   * state always does) changed `onComplete`'s identity too and made
   * it doubly certain.
   *
   * Nothing visible broke, because the pieces render off `isActive`
   * and their CSS animation finishes on its own. What broke was every
   * caller that treated completion as "and now close the dialog" —
   * `CreateProjectModal` did, so a project saved correctly and the
   * sheet sat there looking like it hadn't.
   *
   * Keyed on `trigger` alone now, which is the only thing that should
   * start a run.
   */
  useEffect(() => {
    if (!trigger) return;
    setIsActive(true);
    setPieces(
      Array.from({ length: 50 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        delay: Math.random() * 0.5,
        duration: 2 + Math.random() * 1,
      })),
    );

    const timer = setTimeout(() => {
      setIsActive(false);
      setPieces([]);
      done.current?.();
    }, 3500);

    return () => clearTimeout(timer);
  }, [trigger]);

  if (!isActive || pieces.length === 0) return null;

  return (
    <div className="confetti-container">
      {pieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute w-3 h-3"
          style={{
            left: `${piece.x}%`,
            top: '-20px',
            backgroundColor: piece.color,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            animation: `confetti-fall ${piece.duration}s ease-in-out forwards`,
            animationDelay: `${piece.delay}s`,
            transform: `rotate(${Math.random() * 360}deg)`,
          }}
        />
      ))}
    </div>
  );
}
